import { useEffect, useRef, useState } from 'react';
import { icons } from '~/assets';
import Button from '~/components/button';
import Collapse from '~/components/collapse';
import ContainerFlex from '~/components/containerFlex';
import Dialog from '~/components/dialog';
import LocalDropzone from '~/components/dropZone';
import PictureCard from '~/components/pictureCard';
import { ApiMethod } from '~/hooks/useApi';
import { CompanyDataBodyRequest } from '~/services/banknow/types';
import Endpoints from '~/services/endpoints';
import { httpRequest } from '~/services/httpRequest';
import * as St from '../../styles';

const thumb = {
  display: 'inline-flex',
  borderRadius: 8,
  position: 'relative',
  marginBottom: 8,
  width: 'auto',
  height: 'auto',
  padding: 4,
};

export function EstabilishmentImages({
  company,
  setForm,
  errorMessage,
}: {
  company: CompanyDataBodyRequest;
  setForm: React.Dispatch<React.SetStateAction<CompanyDataBodyRequest>>;
  errorMessage?: string;
}) {
  const dialogInitialState = {
    open: false,
    message: '',
    title: '',
    action: () => {},
    negativeAction: () => {},
    positiveLabel: 'Sim',
  };
  const rendered = useRef(false);
  const [loading, setLoading] = useState(false);
  const [hasChangedImages, setHasChangedImages] = useState(false);
  const [existingImages, setExistingImages] = useState<
    CompanyDataBodyRequest['savedImages']
  >([]); // imagens já no S3

  const [images, setImages] = useState<
    (
      | {
          type: 'existing';
          id: string;
          link: string;
          filename: string;
          mimetype: string;
          delete?: boolean;
        }
      | { type: 'new'; file: File; preview: string }
      | { type: 'recent'; file: File; preview: string; delete: boolean }
    )[]
  >([]);
  const [dialog, setDialog] = useState(dialogInitialState);
  const resetDialog = () => {
    setDialog(dialogInitialState);
  };
  const openCreatindPostDialog = () => {
    const newImages = images?.filter((e) => e.type === 'new');
    const existingImages = images?.filter(
      (e) => e.type === 'existing' || e.type === 'recent',
    );
    const deletingImages = existingImages?.filter((e) => e.delete)?.length || 0;
    setDialog({
      open: true,
      message: `Enviando ${
        newImages.length
      } nova(s) imagem(ns), apagando ${deletingImages} imagem(ns) e mantendo ${
        existingImages.length - deletingImages
      } imagem(ns)`,
      title: `Confirmar envio da(s) imagem(ns)?`,
      action: uploadImage,
      positiveLabel: 'Sim',
      negativeAction: resetDialog,
    });
  };

  const fileToBase64 = async (f: Blob): Promise<string | ArrayBuffer> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(f);
      reader.onload = () =>
        resolve(
          typeof reader.result === 'string'
            ? reader.result?.split(',')[1]
            : reader.result,
        );
      reader.onerror = (e) => reject(e);
    });

  const uploadImage = async () => {
    setLoading(true);
    const existing = images
      .filter((i) => i.type === 'existing')
      .map((i, idx) => ({
        filename: i.filename,
        main: idx === 0,
        mimetype: i.mimetype,
        delete: i.delete,
        link: i.link,
      }));

    const recent = await Promise.all(
      images
        .filter((i) => i.type === 'recent' && !i.link)
        .map(async (i, idx) => ({
          filename: i.file.name,
          main: idx === 0 && existing.length === 0,
          mimetype: i.file.type,
          base64: await fileToBase64(i.file),
          delete: i.delete,
        })),
    );

    const newOnes = await Promise.all(
      images
        .filter((i) => i.type === 'new')
        .map(async (i, idx) => ({
          filename: i.file.name,
          main: idx === 0 && existing.length === 0,
          mimetype: i.file.type,
          base64: await fileToBase64(i.file),
          delete: false,
        })),
    );

    const payload = [...existing, ...newOnes, ...recent];

    try {
      const response = await httpRequest({
        url: Endpoints.companies.putUploadCompanyImages,
        reqParamsURL: { PARAM_ID: company.companyData.id },
        body: { images: payload },
        method: ApiMethod.PUT,
      });

      if (response.status === 200) {
        setDialog({
          open: true,
          message: 'Imagens salvas com sucesso!',
          title: '',
          negativeAction: undefined,
          action: resetDialog,
          positiveLabel: 'Ok',
        });
        setImages((prev) =>
          prev
            .filter((e) => !e.delete)
            .map((e) => ({ ...e, type: e.link ? 'existing' : 'recent' })),
        );
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);

      setDialog({
        open: true,
        message:
          error?.response.status === 413
            ? 'Tamanho das imagens ultrapassou o limite de 10mb'
            : error?.response?.data?.mensagem ??
              'Houve um erro ao salvar as imagens, tente novamente',
        title: 'Ops...',
        negativeAction: null,
        action: resetDialog,
        positiveLabel: 'Ok',
      });
    }
  };

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.dataTransfer.setData('drag-index', index.toString());
    setDraggedIndex(index);
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    dropIndex: number,
  ) => {
    const dragIndex = +e.dataTransfer.getData('drag-index');
    if (isNaN(dragIndex) || dragIndex === dropIndex) return;

    setImages((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(dropIndex, 0, moved);
      return updated;
    });
    setHasChangedImages(true);

    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  console.log('all images', images);
  const thumbs = images.map((file, index) => {
    const isDragging = index === draggedIndex;
    const isOver = index === dragOverIndex && draggedIndex !== index;
    const isExisting = !!file.link;
    const justAdded = file.type === 'recent';
    return (
      <div
        key={isExisting ? file.filename : file.name}
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDrop={(e) => handleDrop(e, index)}
        onDragEnd={handleDragEnd}
      >
        <div>
          <PictureCard
            style={{
              ...thumb,
              zIndex: 1,
              cursor: 'grab',
              opacity: isDragging ? 0.4 : 1,
              transform: isOver ? 'scale(1.05)' : 'none',
              translate: isOver ? '30% 0%' : 'none',
              border: isOver
                ? '2px dashed #007bff'
                : isDragging
                ? '2px solid #aaa'
                : '',
              transition: 'all 0.15s ease',
              ...((isExisting || justAdded) &&
                file?.delete && {
                  opacity: 0.3,
                  transform: 'scale(0.8)',
                }),
            }}
            onClickCancelDelete={() => {
              setImages((prev) =>
                prev
                  .map((img, i) => {
                    // novo → remove direto
                    if (i === index && img.type === 'new') return null;

                    // existente → marca para exclusão
                    if (
                      i === index &&
                      (img.type === 'existing' || img.type === 'recent')
                    ) {
                      return { ...img, delete: false };
                    }

                    return img;
                  })
                  .filter(Boolean),
              );
            }}
            onClickDelete={() => {
              setHasChangedImages(true);
              setImages((prev) =>
                prev
                  .map((img, i) => {
                    // novo → remove direto
                    if (i === index && img.type === 'new') return null;

                    // existente → marca para exclusão
                    if (
                      i === index &&
                      (img.type === 'existing' || img.type === 'recent')
                    ) {
                      return { ...img, delete: true };
                    }

                    return img;
                  })
                  .filter(Boolean),
              );
            }}
            width={window.screen.availWidth / 4}
            height={230}
            showOptions
            src={isExisting ? file.link : file.preview}
          />
        </div>
      </div>
    );
  });
  useEffect(() => {
    if (!company.companyData.id) {
      setForm((prev) => ({
        ...prev,
        newImages: images.filter((e) => e.type === 'new'),
      }));
    }
  }, [images, company.companyData.id]);
  const savedImages = company.savedImages;
  useEffect(() => {
    if (rendered.current) return;
    if (savedImages?.length > 0) {
      rendered.current = true;
      setExistingImages(
        savedImages.map((img) => ({
          type: 'existing',
          id: img.id ?? 'sem id',
          link: img.link,
          main: img.main,
          filename: img.filename,
          mimetype: img.mimetype,
          delete: false,
        })),
      );
      setImages(
        savedImages.map((img) => ({
          type: 'existing',
          id: img.id ?? 'sem id',
          link: img.link,
          main: img.main,
          filename: img.filename,
          mimetype: img.mimetype,
          delete: false,
        })),
      );
    }
  }, [savedImages]);
  // useEffect(() => {
  //   return () =>
  //     images
  //       .filter((e) => e.type !== 'existing')
  //       .forEach((file) => URL.revokeObjectURL(file?.preview));
  // }, [images]);

  return (
    <>
      <Dialog
        icon={icons.warning}
        open={dialog.open}
        title={dialog?.title}
        message={dialog.message}
        positiveAction={() => dialog.action && dialog.action()}
        negativeAction={dialog.negativeAction ? () => resetDialog() : null}
        positiveLabel={dialog.positiveLabel ?? 'SIM'}
        negativeLabel={dialog.negativeAction ? 'NÃO' : undefined}
        widthInPixel={2}
        buttonDisabledPositive={loading}
        Loading={loading}
      />
      <Collapse
        collapseHeight={images.length * 12}
        title="Imagens"
        initialState="close"
        titlePadding={5}
      >
        <ContainerFlex flex={1} row wrap>
          <St.TitleHeader>Selecione um máximo de 3 imagens!</St.TitleHeader>
          <St.ContainerDropButton>
            <LocalDropzone
              containerStyle={{ width: '100%', marginLeft: 0 }}
              label="Selecione ou arraste as imagens da empresa"
              dropZoneOptions={{
                multiple: true,
                maxFiles: 3,
                maxSize: 10000000,
                onDrop(acceptedFiles, fileRejections, event) {
                  if (acceptedFiles.length + images.length > 3) {
                    setDialog({
                      open: true,
                      message: 'O máximo de imagens permitidas são 3',
                      title: 'Ops',
                      negativeAction: null,
                      action: resetDialog,
                      positiveLabel: 'Ok',
                    });
                    return;
                  }
                  const formatted = acceptedFiles.map((file) => ({
                    type: 'new',
                    file,
                    preview: URL.createObjectURL(file),
                  }));
                  setImages((prev) => [...prev, ...formatted]);
                  setHasChangedImages(true);
                },
              }}
            />
            {hasChangedImages && company.companyData.id && (
              <St.ContainerButtons>
                <Button outlined onClick={() => setImages(existingImages)}>
                  Cancelar/Restaurar
                </Button>
                <Button onClick={() => openCreatindPostDialog()}>Enviar</Button>
              </St.ContainerButtons>
            )}
            <St.ThumbnailContainer>{thumbs}</St.ThumbnailContainer>
          </St.ContainerDropButton>
        </ContainerFlex>
      </Collapse>
      {errorMessage && <St.SpanLabelFeed>{errorMessage}</St.SpanLabelFeed>}
    </>
  );
}
